import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';

const PortalContext = createContext();

export const usePortal = () => {
  const context = useContext(PortalContext);
  if (!context) {
    throw new Error('usePortal must be used within a PortalProvider');
  }
  return context;
};

export const PortalProvider = ({ children }) => {
  const [portalMode, setPortalMode] = useState('mds'); // 'mds' or 'report'

  // Initialize menu collapsed state based on device type
  // On mobile (width < 768px), menu should always start collapsed
  const isMobileDevice = () => window.innerWidth < 768;
  const [isReportMenuCollapsed, setIsReportMenuCollapsed] = useState(isMobileDevice());

  // Mobile sticky header state for PowerBI reports
  const [showMobileStickyHeader, setShowMobileStickyHeader] = useState(false);

  // Page change handler - will be set by PowerBIPortal component
  const [handlePageChange, setHandlePageChange] = useState(null);

  // Current active page - will be set by PowerBIPortal component
  const [currentActivePage, setCurrentActivePage] = useState(null);

  const togglePortal = useCallback(() => {
    setPortalMode((prev) => (prev === 'mds' ? 'report' : 'mds'));
  }, []);

  const setMDSMode = useCallback(() => setPortalMode('mds'), []);
  const setReportMode = useCallback(() => setPortalMode('report'), []);

  const toggleReportMenu = useCallback(() => {
    setIsReportMenuCollapsed((prev) => !prev);
  }, []);

  // Handle window resize - auto-collapse menu on mobile
  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth < 768;
      // If switching to mobile view, always collapse the menu
      if (isMobile && !isReportMenuCollapsed) {
        setIsReportMenuCollapsed(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isReportMenuCollapsed]);

  const value = useMemo(
    () => ({
      portalMode,
      isMDSMode: portalMode === 'mds',
      isReportMode: portalMode === 'report',
      togglePortal,
      setMDSMode,
      setReportMode,
      isReportMenuCollapsed,
      toggleReportMenu,
      showMobileStickyHeader,
      setShowMobileStickyHeader,
      handlePageChange,
      setHandlePageChange,
      currentActivePage,
      setCurrentActivePage,
    }),
    [portalMode, togglePortal, setMDSMode, setReportMode, isReportMenuCollapsed, toggleReportMenu, showMobileStickyHeader, handlePageChange, currentActivePage]
  );

  return (
    <PortalContext.Provider value={value}>
      {children}
    </PortalContext.Provider>
  );
};
